import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../../lib/api';
import { formatCurrency } from '../../../utils/formatters';
import './TrendRealeFinanziamento.css';

interface RataTeorica {
  id: number;
  numeroRata: number;
  dataScadenza: string;
  importoRata: number;
  quotaCapitale: number;
  quotaInteressi: number;
  isPagata: boolean;
}

interface Movimento {
  id: number;
  dataPagamento: string;
  quotaInteressi: number;
  quotaCapitale: number;
  spese: number;
  rataTeoricaId: number | null;
}

interface Props {
  finanziamentoId: number;
  rateTeoriche: RataTeorica[];
}

interface RigaPiano {
  numeroRata: number;
  dataScadenza: string;
  qiTeorica: number;
  qcTeorica: number;
  qiReale?: number;
  qcReale?: number;
  spese?: number;
  deltaQi?: number;
  deltaQc?: number;
  isPagata: boolean;
  movimentoId?: number;
  isProiezione?: boolean;
}

const fmtData = (d: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function TrendRealeFinanziamento({ finanziamentoId, rateTeoriche }: Props) {
  const [movimenti, setMovimenti] = useState<Movimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function fetchMovimenti() {
      setLoading(true);
      setError('');
      try {
        // @api GET /api/movimenti-finanziamento?finanziamentoId= -> { success: true, data: [{ id, dataPagamento, quotaInteressi, quotaCapitale, spese, rataTeoricaId }] }
        const res = await apiFetch<{ success: boolean; data: Movimento[] }>(
          `/api/movimenti-finanziamento?finanziamentoId=${finanziamentoId}`,
        );
        if (!cancelled) setMovimenti(res.data ?? []);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Errore nel caricamento movimenti');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchMovimenti();
    return () => { cancelled = true; };
  }, [finanziamentoId]);

  // Costruisce la tabella: per ogni rata teorica trova il movimento collegato
  const { righe, delta, totali, movExtraPiano } = useMemo(() => {
    const movByRata = new Map<number, Movimento>();
    const extraPiano: Movimento[] = [];
    for (const m of movimenti) {
      if (m.rataTeoricaId) movByRata.set(m.rataTeoricaId, m);
      else extraPiano.push(m);
    }

    const righe: RigaPiano[] = [];
    let totQiTeo = 0, totQcTeo = 0, totQiReale = 0, totQcReale = 0;
    let countPagate = 0;
    let sommaDeltaQi = 0;

    for (const r of rateTeoriche) {
      const mov = movByRata.get(r.id);
      const riga: RigaPiano = {
        numeroRata: r.numeroRata,
        dataScadenza: r.dataScadenza,
        qiTeorica: Number(r.quotaInteressi),
        qcTeorica: Number(r.quotaCapitale),
        isPagata: r.isPagata,
      };
      totQiTeo += riga.qiTeorica;
      totQcTeo += riga.qcTeorica;
      if (mov) {
        riga.qiReale = Number(mov.quotaInteressi);
        riga.qcReale = Number(mov.quotaCapitale);
        riga.spese = Number(mov.spese);
        riga.deltaQi = riga.qiReale - riga.qiTeorica;
        riga.deltaQc = riga.qcReale - riga.qcTeorica;
        riga.movimentoId = mov.id;
        totQiReale += riga.qiReale;
        totQcReale += riga.qcReale;
        if (riga.qiTeorica > 0) {
          sommaDeltaQi += (riga.qiReale - riga.qiTeorica) / riga.qiTeorica;
          countPagate += 1;
        }
      }
      righe.push(riga);
    }

    // Delta empirico medio (solo su rate effettivamente pagate)
    const deltaPct = countPagate > 0 ? sommaDeltaQi / countPagate : 0;

    // Proiezione futura: per ogni rata teorica futura senza movimento, applica delta
    for (const r of righe) {
      if (!r.movimentoId) {
        r.isProiezione = true;
        // proiezione visiva: interessi = teorici * (1 + delta empirico)
        // capitale invariato (è certo da contratto)
      }
    }

    return {
      righe,
      delta: { pct: deltaPct, count: countPagate },
      totali: {
        qiTeo: totQiTeo,
        qcTeo: totQcTeo,
        qiReale: totQiReale,
        qcReale: totQcReale,
        deltaQi: totQiReale - (rateTeoriche.filter(r => movByRata.has(r.id)).reduce((s, r) => s + Number(r.quotaInteressi), 0)),
      },
      movExtraPiano: extraPiano,
    };
  }, [movimenti, rateTeoriche]);

  if (loading) {
    return (
      <div className="bop-card">
        <div className="bop-empty-state">Caricamento movimenti…</div>
      </div>
    );
  }

  if (error) {
    return <div className="bop-inline-error">{error}</div>;
  }

  if (movimenti.length === 0) {
    return (
      <div className="bop-card">
        <div className="bop-empty-state">
          Nessun movimento reale caricato per questo finanziamento.<br />
          Vai a <strong>Movimenti Finanziamenti</strong> per inserire i pagamenti rate dall'estratto conto.
        </div>
      </div>
    );
  }

  return (
    <div className="trend-reale">

      {/* Card riassuntiva */}
      <div className="trend-reale__summary">
        <div className="trend-reale__summary-card">
          <div className="trend-reale__summary-label">Rate con pagamento reale</div>
          <div className="trend-reale__summary-value">{delta.count}</div>
        </div>
        <div className="trend-reale__summary-card">
          <div className="trend-reale__summary-label">Delta interessi medio</div>
          <div className={`trend-reale__summary-value ${delta.pct > 0 ? 'trend-reale__summary-value--alert' : 'trend-reale__summary-value--ok'}`}>
            {delta.count === 0 ? '—' : `${(delta.pct * 100).toFixed(1)}%`}
          </div>
          <div className="trend-reale__summary-sub">
            {delta.pct > 0 ? 'Pagati più del piano' : delta.pct < 0 ? 'Pagati meno del piano' : '—'}
          </div>
        </div>
        <div className="trend-reale__summary-card">
          <div className="trend-reale__summary-label">Interessi teorici (su pagate)</div>
          <div className="trend-reale__summary-value">{formatCurrency(rateTeoriche.filter(r => movimenti.some(m => m.rataTeoricaId === r.id)).reduce((s, r) => s + Number(r.quotaInteressi), 0))}</div>
        </div>
        <div className="trend-reale__summary-card">
          <div className="trend-reale__summary-label">Interessi reali pagati</div>
          <div className="trend-reale__summary-value">{formatCurrency(totali.qiReale)}</div>
        </div>
      </div>

      {delta.count > 0 && (
        <div className="trend-reale__note">
          Le rate future mostrano una <strong>proiezione</strong> ottenuta applicando il delta empirico medio
          ({(delta.pct * 100).toFixed(1)}%) agli interessi teorici. Il capitale resta quello del piano (certo da contratto).
          La proiezione si stabilizza man mano che vengono caricati più movimenti reali.
        </div>
      )}

      {/* Tabella affiancata */}
      <div className="bop-card">
        <table className="bop-table trend-reale__table">
          <thead>
            <tr>
              <th>Rata</th>
              <th>Scadenza</th>
              <th className="bop-text-right">Cap. teorico</th>
              <th className="bop-text-right">Cap. reale</th>
              <th className="bop-text-right">Δ capitale</th>
              <th className="bop-text-right">Int. teorici</th>
              <th className="bop-text-right">Int. reali / proiezione</th>
              <th className="bop-text-right">Δ interessi</th>
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {righe.map(r => {
              const isProiezione = r.isProiezione && delta.count > 0;
              const intStimati = isProiezione ? r.qiTeorica * (1 + delta.pct) : r.qiReale;
              const deltaInt = isProiezione ? intStimati! - r.qiTeorica : r.deltaQi;

              return (
                <tr key={r.numeroRata} className={isProiezione ? 'trend-reale__row--proiezione' : ''}>
                  <td>{r.numeroRata}</td>
                  <td>{fmtData(r.dataScadenza)}</td>
                  <td className="bop-text-right">{formatCurrency(r.qcTeorica)}</td>
                  <td className="bop-text-right">{r.qcReale != null ? formatCurrency(r.qcReale) : '—'}</td>
                  <td className={`bop-text-right ${(r.deltaQc ?? 0) > 0 ? 'trend-reale__delta--neg' : (r.deltaQc ?? 0) < 0 ? 'trend-reale__delta--pos' : ''}`}>
                    {r.deltaQc != null ? formatCurrency(r.deltaQc) : '—'}
                  </td>
                  <td className="bop-text-right">{formatCurrency(r.qiTeorica)}</td>
                  <td className={`bop-text-right ${isProiezione ? 'trend-reale__cell--proiezione' : ''}`}>
                    {intStimati != null ? formatCurrency(intStimati) : '—'}
                  </td>
                  <td className={`bop-text-right ${(deltaInt ?? 0) > 0 ? 'trend-reale__delta--neg' : (deltaInt ?? 0) < 0 ? 'trend-reale__delta--pos' : ''}`}>
                    {deltaInt != null ? formatCurrency(deltaInt) : '—'}
                  </td>
                  <td>
                    {r.movimentoId
                      ? <span className="bop-badge bop-badge-success">Reale</span>
                      : (isProiezione ? <span className="bop-badge bop-badge-warning">Proiezione</span> : <span className="bop-badge bop-badge-neutral">Teorica</span>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Eventuali movimenti extra-piano */}
      {movExtraPiano.length > 0 && (
        <div className="bop-card trend-reale__extra">
          <div className="bop-finanz__section-title">Movimenti extra-piano</div>
          <p className="bop-text-muted">
            Pagamenti non collegati a una rata teorica (rinegoziazioni, estinzioni parziali, ecc.).
          </p>
          <table className="bop-table">
            <thead>
              <tr>
                <th>Data pagamento</th>
                <th className="bop-text-right">Quota capitale</th>
                <th className="bop-text-right">Quota interessi</th>
                <th className="bop-text-right">Spese</th>
              </tr>
            </thead>
            <tbody>
              {movExtraPiano.map(m => (
                <tr key={m.id}>
                  <td>{fmtData(m.dataPagamento)}</td>
                  <td className="bop-text-right">{formatCurrency(m.quotaCapitale)}</td>
                  <td className="bop-text-right">{formatCurrency(m.quotaInteressi)}</td>
                  <td className="bop-text-right">{formatCurrency(m.spese)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
